/**
 * ControlledSubstancePage
 * CCH Care — Phase 4d
 *
 * Three tabs:
 * 1. Counts — shift-change controlled substance counts
 * 2. Waste  — document and view waste records
 * 3. Discrepancies — view and resolve count discrepancies
 *
 * Changes (2026-04-18):
 * - Removed unused `role` prop plumbing from CountsTab / WasteTab /
 *   DiscrepanciesTab. Was destructured from useAuth and passed through
 *   but never consumed inside. If role gating is added later, reinstate
 *   intentionally at that time.
 * - Fixed COLORS.x + '44' pattern (produced invalid CSS like
 *   `var(--color-accent)44` in two places). Replaced with semantic
 *   *Light tokens for softer tints.
 * - Dark mode: replaced hardcoded '#fff' surfaces, '#FDEDEC' danger
 *   tints with COLORS.surface and COLORS.dangerLight.
 */
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../config/constants';
import client from '../../api/client';

import { formatDate, formatDateTime } from '../../utils/formatters';
const BASE = '/adl/controlled.php';
const SHIFT_BASE = '/admin/care-shifts.php';

function localDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

const WASTE_REASONS = {
  partial_dose:       'Partial Dose Remaining',
  refused_after_draw: 'Refused After Draw',
  spillage:           'Spillage',
  contamination:      'Contamination',
  other:              'Other',
};

const DISPOSAL_METHODS = {
  sink:                'Sink',
  sharps_container:    'Sharps Container',
  waste_bin:           'Waste Bin',
  reverse_distributor: 'Reverse Distributor',
  other:               'Other',
};

export default function ControlledSubstancePage() {
  const navigate   = useNavigate();
  const [tab, setTab] = useState('counts');
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', background: COLORS.primary, color: '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate('/dashboard/more')} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', padding: '4px 8px' }}>← Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Controlled Substances</div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>{today}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}` }}>
        {[['counts', '🔢 Counts'], ['waste', '🗑️ Waste'], ['discrepancies', '⚠️ Discrepancies']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            flex: 1, padding: '12px 8px', border: 'none', background: 'none',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            color: tab === key ? COLORS.primary : COLORS.textMuted,
            borderBottom: tab === key ? `2px solid ${COLORS.primary}` : '2px solid transparent',
          }}>{label}</button>
        ))}
      </div>

      {tab === 'counts'        && <CountsTab />}
      {tab === 'waste'         && <WasteTab />}
      {tab === 'discrepancies' && <DiscrepanciesTab />}
    </div>
  );
}

// ── COUNTS TAB ───────────────────────────────────────────────
function CountsTab() {
  const [shifts, setShifts]         = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [countType, setCountType]   = useState('opening');
  const [medications, setMedications] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [countModal, setCountModal] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Load today's shifts
  useEffect(() => {
    client.get(`${SHIFT_BASE}?date=${localDate()}`)
      .then(r => { if (r.data.success) setShifts(r.data.shifts || []); })
      .catch(() => {});
  }, []);

  const loadPending = useCallback(async () => {
    if (!selectedShift) return;
    setLoading(true); setError(null);
    try {
      const res = await client.get(`${BASE}?action=pending_count&care_shift_id=${selectedShift.care_shift_id}&count_type=${countType}`);
      if (res.data.success) setMedications(res.data.medications || []);
      else setError(res.data.error || 'Failed to load');
    } catch { setError('Failed to load medications'); }
    finally { setLoading(false); }
  }, [selectedShift, countType]);

  useEffect(() => { loadPending(); }, [loadPending]);

  const handleSubmitCount = async (formData) => {
    setSaving(true); setError(null);
    try {
      const res = await client.post(`${BASE}?action=submit_count`, {
        care_shift_id: selectedShift.care_shift_id,
        medication_id: countModal.medication_id,
        count_type:    countType,
        ...formData,
      });
      if (res.data.success) {
        setCountModal(null);
        if (res.data.has_discrepancy) {
          setSuccessMsg(`⚠️ Count submitted — discrepancy detected! Expected: ${res.data.expected_count}, Actual: ${res.data.actual_count}. A discrepancy report has been created.`);
        } else {
          setSuccessMsg('✓ Count submitted successfully — counts match.');
        }
        loadPending();
        setTimeout(() => setSuccessMsg(null), 6000);
      } else setError(res.data.error || 'Failed to submit count');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to submit count'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Shift selector */}
      <div style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, padding: '14px 16px', marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, marginBottom: 8, textTransform: 'uppercase' }}>Select Shift</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {shifts.length === 0 ? (
            <div style={{ fontSize: 13, color: COLORS.textMuted }}>No shifts found for today.</div>
          ) : shifts.map(s => (
            <button key={s.care_shift_id} onClick={() => setSelectedShift(s)} style={{
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: selectedShift?.care_shift_id === s.care_shift_id ? COLORS.primary : COLORS.surface,
              color: selectedShift?.care_shift_id === s.care_shift_id ? '#fff' : COLORS.textMuted,
              border: `1px solid ${selectedShift?.care_shift_id === s.care_shift_id ? COLORS.primary : COLORS.border}`,
            }}>{s.shift_name} <span style={{ opacity: 0.7 }}>({s.status})</span></button>
          ))}
        </div>
        {selectedShift && (
          <div style={{ display: 'flex', gap: 8 }}>
            {['opening','closing'].map(ct => (
              <button key={ct} onClick={() => setCountType(ct)} style={{
                padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                background: countType === ct ? COLORS.accent : COLORS.surface,
                color: countType === ct ? '#fff' : COLORS.textMuted,
                border: `1px solid ${countType === ct ? COLORS.accent : COLORS.border}`,
              }}>{ct === 'opening' ? '🔓 Opening Count' : '🔒 Closing Count'}</button>
            ))}
          </div>
        )}
      </div>

      {successMsg && (
        <div style={{ padding: '12px 14px', borderRadius: 8, background: successMsg.includes('⚠️') ? COLORS.warningLight : COLORS.accentLight, border: `1px solid ${successMsg.includes('⚠️') ? COLORS.warning : COLORS.accent}`, fontSize: 13, marginBottom: 12 }}>
          {successMsg}
        </div>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {!selectedShift ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔢</div>
          <div>Select a shift above to begin the count.</div>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>Loading controlled medications...</div>
      ) : medications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
          <div>No controlled substances on file for this facility.</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 8 }}>
            {medications.filter(m => m.already_counted).length}/{medications.length} medications counted
          </div>
          {medications.map(med => (
            <div key={med.medication_id} style={{
              background: COLORS.surface,
              borderRadius: 10,
              border: `1px solid ${med.already_counted ? COLORS.accent : COLORS.border}`,
              marginBottom: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{med.drug_name}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.danger, background: COLORS.dangerLight, padding: '1px 5px', borderRadius: 4 }}>C-{med.controlled_schedule}</span>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                  {parseFloat(med.dose)} {med.dose_unit} · {med.first_name} {med.last_name}
                </div>
                <div style={{ fontSize: 11, color: COLORS.primary, marginTop: 2 }}>
                  Expected count: {parseFloat(med.expected_count)}
                </div>
              </div>
              {med.already_counted ? (
                <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent }}>✓ Counted</span>
              ) : (
                <button onClick={() => setCountModal(med)} style={{
                  padding: '7px 14px', borderRadius: 8, border: 'none',
                  background: COLORS.primary, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>Count</button>
              )}
            </div>
          ))}
        </>
      )}

      {countModal && (
        <CountModal
          med={countModal}
          countType={countType}
          onConfirm={handleSubmitCount}
          onCancel={() => setCountModal(null)}
          saving={saving}
          error={error}
        />
      )}
    </div>
  );
}

// ── WASTE TAB ─────────────────────────────────────────────────
function WasteTab() {
  const [wasteRecords, setWasteRecords] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [wasteModal, setWasteModal]     = useState(false);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState(null);

  const loadWaste = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`${BASE}?action=waste_list`);
      if (res.data.success) setWasteRecords(res.data.waste_records || []);
    } catch { setError('Failed to load waste records'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadWaste(); }, [loadWaste]);

  const handleSubmitWaste = async (formData) => {
    setSaving(true); setError(null);
    try {
      const res = await client.post(`${BASE}?action=submit_waste`, formData);
      if (res.data.success) { setWasteModal(false); loadWaste(); }
      else setError(res.data.error || 'Failed to document waste');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to document waste'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: COLORS.textMuted }}>All waste documentation this facility.</div>
        <button onClick={() => setWasteModal(true)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          + Document Waste
        </button>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>Loading...</div>
      ) : wasteRecords.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🗑️</div>
          <div>No waste records on file.</div>
        </div>
      ) : wasteRecords.map(r => (
        <div key={r.waste_id} style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, marginBottom: 8, padding: '12px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{r.drug_name}</span>
            <span style={{ fontSize: 11, color: COLORS.textMuted }}>{formatDateTime(r.wasted_at)}</span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            {parseFloat(r.dose_wasted)} {r.dose_unit} wasted · {WASTE_REASONS[r.waste_reason] || r.waste_reason}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
            {r.first_name} {r.last_name} · By {r.wasted_by_name} · Witnessed by {r.witness_name}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted }}>Disposal: {DISPOSAL_METHODS[r.disposal_method] || r.disposal_method}</div>
          {r.notes && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>Note: {r.notes}</div>}
        </div>
      ))}

      {wasteModal && (
        <WasteModal onConfirm={handleSubmitWaste} onCancel={() => setWasteModal(false)} saving={saving} error={error} />
      )}
    </div>
  );
}

// ── DISCREPANCIES TAB ─────────────────────────────────────────
function DiscrepanciesTab() {
  const [discrepancies, setDiscrepancies] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [statusFilter, setStatusFilter]   = useState('open');
  const [resolveModal, setResolveModal]   = useState(null);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState(null);

  const loadDiscrepancies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`${BASE}?action=discrepancies&status=${statusFilter}`);
      if (res.data.success) setDiscrepancies(res.data.discrepancies || []);
    } catch { setError('Failed to load discrepancies'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { loadDiscrepancies(); }, [loadDiscrepancies]);

  const handleResolve = async (formData) => {
    setSaving(true); setError(null);
    try {
      const res = await client.post(`${BASE}?action=resolve`, { discrepancy_id: resolveModal.discrepancy_id, ...formData });
      if (res.data.success) { setResolveModal(null); loadDiscrepancies(); }
      else setError(res.data.error || 'Failed to resolve');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to resolve'); }
    finally { setSaving(false); }
  };

  // Status card colors — uses the solid color for border/label and the
  // existing *Light token for the background tint. Previously concatenated
  // `+ '44'` hex alpha onto COLORS tokens which resolve to CSS vars and
  // produced invalid CSS.
  const STATUS_CONFIG = {
    open:                 { label: 'Open',                 color: COLORS.danger,  bg: COLORS.dangerLight,  border: COLORS.danger },
    under_investigation:  { label: 'Under Investigation',  color: COLORS.warning, bg: COLORS.warningLight, border: COLORS.warning },
    resolved:             { label: 'Resolved',             color: COLORS.accent,  bg: COLORS.accentLight,  border: COLORS.accent },
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['open','under_investigation','resolved','all'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{
            padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            background: statusFilter === s ? COLORS.primary : COLORS.surface,
            color: statusFilter === s ? '#fff' : COLORS.textMuted,
            border: `1px solid ${statusFilter === s ? COLORS.primary : COLORS.border}`,
          }}>{s === 'under_investigation' ? 'Investigating' : s.charAt(0).toUpperCase() + s.slice(1)}</button>
        ))}
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>Loading...</div>
      ) : discrepancies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
          <div>No {statusFilter === 'all' ? '' : statusFilter} discrepancies found.</div>
        </div>
      ) : discrepancies.map(d => {
        const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.open;
        return (
          <div key={d.discrepancy_id} style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${cfg.border}`, marginBottom: 10, overflow: 'hidden' }}>
            <div style={{ padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{d.drug_name}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.danger, background: COLORS.dangerLight, padding: '1px 5px', borderRadius: 4 }}>C-{d.controlled_schedule}</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: '2px 8px', borderRadius: 6 }}>{cfg.label}</span>
              </div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                {d.first_name} {d.last_name} · {d.shift_name} · {formatDate(d.reported_at)}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: COLORS.textMuted }}>Expected: </span>
                  <span style={{ fontWeight: 700, color: COLORS.text }}>{parseFloat(d.expected_count)}</span>
                </div>
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: COLORS.textMuted }}>Actual: </span>
                  <span style={{ fontWeight: 700, color: COLORS.danger }}>{parseFloat(d.actual_count)}</span>
                </div>
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: COLORS.textMuted }}>Difference: </span>
                  <span style={{ fontWeight: 700, color: COLORS.danger }}>{parseFloat(d.discrepancy_amount) > 0 ? '+' : ''}{parseFloat(d.discrepancy_amount)}</span>
                </div>
              </div>
              {d.resolution_notes && (
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 6, fontStyle: 'italic' }}>
                  Resolution: {d.resolution_notes}
                </div>
              )}
            </div>
            {d.status !== 'resolved' && (
              <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: '8px 14px', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setResolveModal(d)} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Update / Resolve
                </button>
              </div>
            )}
          </div>
        );
      })}

      {resolveModal && (
        <ResolveModal discrepancy={resolveModal} onConfirm={handleResolve} onCancel={() => setResolveModal(null)} saving={saving} error={error} />
      )}
    </div>
  );
}

// ── Modals ────────────────────────────────────────────────────

function CountModal({ med, countType, onConfirm, onCancel, saving, error }) {
  const [actualCount, setActualCount] = useState('');
  const [witnessId, setWitnessId]     = useState('');
  const [notes, setNotes]             = useState('');

  const hasDiscrepancy = actualCount !== '' && parseFloat(actualCount) !== parseFloat(med.expected_count);

  return (
    <Modal title={`${countType === 'opening' ? 'Opening' : 'Closing'} Count: ${med.drug_name}`} onClose={onCancel}>
      <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 14 }}>
        {parseFloat(med.dose)} {med.dose_unit} · Schedule {med.controlled_schedule} · {med.first_name} {med.last_name}
      </div>
      <div style={{ background: COLORS.primaryLight, borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: COLORS.textMuted }}>Expected Count</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.primary }}>{parseFloat(med.expected_count)}</div>
      </div>
      <FormField label="Actual Physical Count *" value={actualCount} onChange={setActualCount} type="number" placeholder="Enter count" inputId="count-actual" />
      {hasDiscrepancy && actualCount !== '' && (
        <div style={{ background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`, borderRadius: 8, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: COLORS.danger, fontWeight: 600 }}>
          ⚠️ Discrepancy detected: difference of {(parseFloat(actualCount) - parseFloat(med.expected_count)).toFixed(3)}. A discrepancy report will be created automatically.
        </div>
      )}
      <FormField label="Witness Membership ID *" value={witnessId} onChange={setWitnessId} type="number" placeholder="Enter witness staff ID" inputId="count-witness" />
      <FormField label="Notes (optional)" value={notes} onChange={setNotes} placeholder="Any additional observations..." inputId="count-notes" />
      {error && <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onConfirm({ actual_count: parseFloat(actualCount), witness_id: parseInt(witnessId), notes })}
          disabled={saving || !actualCount || !witnessId}
          style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: hasDiscrepancy ? COLORS.danger : COLORS.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Submitting…' : hasDiscrepancy ? 'Submit with Discrepancy' : 'Submit Count'}
        </button>
      </div>
    </Modal>
  );
}

function WasteModal({ onConfirm, onCancel, saving, error }) {
  const [form, setForm] = useState({
    medication_id: '', patient_id: '', dose_wasted: '',
    dose_unit: 'mg', waste_reason: 'partial_dose',
    disposal_method: 'waste_bin', witness_id: '', notes: '',
  });
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Modal title="Document Waste" onClose={onCancel}>
      <div style={{ background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`, borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: COLORS.danger, fontWeight: 600 }}>
        ⚠️ Controlled Substance Waste — witness required
      </div>
      <FormField label="Medication ID *" value={form.medication_id} onChange={v => u('medication_id', v)} type="number" placeholder="Enter medication ID" inputId="waste-medication-id" />
      <FormField label="Patient ID *" value={form.patient_id} onChange={v => u('patient_id', v)} type="number" placeholder="Enter patient ID" inputId="waste-patient-id" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <FormField label="Amount Wasted *" value={form.dose_wasted} onChange={v => u('dose_wasted', v)} type="number" placeholder="e.g. 2.5" inputId="waste-dose" />
        <FormSelect label="Unit *" value={form.dose_unit} onChange={v => u('dose_unit', v)} options={['mg','mcg','g','ml','units','mEq','drops','puffs','other']} selectId="waste-unit" />
      </div>
      <FormSelect label="Reason *" value={form.waste_reason} onChange={v => u('waste_reason', v)} options={Object.keys(WASTE_REASONS)} labels={WASTE_REASONS} selectId="waste-reason" />
      <FormSelect label="Disposal Method *" value={form.disposal_method} onChange={v => u('disposal_method', v)} options={Object.keys(DISPOSAL_METHODS)} labels={DISPOSAL_METHODS} selectId="waste-disposal" />
      <FormField label="Witness Membership ID *" value={form.witness_id} onChange={v => u('witness_id', v)} type="number" placeholder="Enter witness staff ID" inputId="waste-witness" />
      <FormField label="Notes (optional)" value={form.notes} onChange={v => u('notes', v)} placeholder="Additional details..." inputId="waste-notes" />
      {error && <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onConfirm({ ...form, medication_id: parseInt(form.medication_id), patient_id: parseInt(form.patient_id), witness_id: parseInt(form.witness_id), dose_wasted: parseFloat(form.dose_wasted) })}
          disabled={saving || !form.medication_id || !form.patient_id || !form.dose_wasted || !form.witness_id}
          style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Document Waste'}
        </button>
      </div>
    </Modal>
  );
}

function ResolveModal({ discrepancy, onConfirm, onCancel, saving, error }) {
  const [status, setStatus]           = useState('resolved');
  const [resolutionNotes, setNotes]   = useState('');

  return (
    <Modal title="Update Discrepancy" onClose={onCancel}>
      <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 14 }}>
        {discrepancy.drug_name} — Expected: {parseFloat(discrepancy.expected_count)}, Actual: {parseFloat(discrepancy.actual_count)}
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Status *</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['under_investigation','Under Investigation'], ['resolved','Resolved']].map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} style={{
              flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: status === v ? COLORS.primary : COLORS.surface,
              color: status === v ? '#fff' : COLORS.textMuted,
              border: `1px solid ${status === v ? COLORS.primary : COLORS.border}`,
            }}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label htmlFor="resolve-notes" style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>Resolution Notes *</label>
        <textarea id="resolve-notes" value={resolutionNotes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Explain the discrepancy and resolution..."
          style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', background: COLORS.surface, color: COLORS.text }} />
      </div>
      {error && <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onConfirm({ status, resolution_notes: resolutionNotes })}
          disabled={saving || !resolutionNotes}
          style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: (saving || !resolutionNotes) ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Update'}
        </button>
      </div>
    </Modal>
  );
}

// ── Shared helpers ────────────────────────────────────────────

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: COLORS.surface, borderRadius: 12, padding: 24, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: COLORS.text }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: COLORS.textMuted }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = 'text', inputId }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label htmlFor={inputId} style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>}
      <input id={inputId} type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', background: COLORS.surface, color: COLORS.text }} />
    </div>
  );
}

function FormSelect({ label, value, onChange, options, labels, selectId }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label htmlFor={selectId} style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>}
      <select id={selectId} value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, background: COLORS.surface, color: COLORS.text, boxSizing: 'border-box' }}>
        {options.map(o => <option key={o} value={o}>{labels ? labels[o] : o}</option>)}
      </select>
    </div>
  );
}

function ErrorBanner({ message, onDismiss }) {
  return (
    <div style={{ padding: '10px 14px', borderRadius: 8, background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`, fontSize: 13, color: COLORS.danger, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
      {message}
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer' }}>✕</button>
    </div>
  );
}
